import React, { useState } from 'react';
import { AlertTriangle, ArrowRight } from 'lucide-react';
import { postSection } from './shared';
import { toast } from 'sonner';

export function ExecutionEditor({ data, meta, onSaved, projectId, date, deptLabel, readOnly = false }) {
  const lastCum = meta?.last_cumulative_by_boq || {};
  const [rows, setRows] = useState(() =>
    (data.activities || []).length
      ? data.activities.map((a) => ({ ...a }))
      : [{ description: '', wbs_id: '', boq_id: '', daily_qty: 0, yesterday_cumulative: 0, boq_qty: 0 }]
  );
  const [saving, setSaving] = useState(false);

  const updateRow = (i, key, val) => {
    const next = rows.map((r, idx) => idx === i ? { ...r, [key]: val } : r);
    if (key === 'boq_id') {
      const boq = (meta?.boq_items || []).find(b => b.id === val);
      if (boq) {
        next[i].boq_qty = boq.quantity || 0;
        next[i].yesterday_cumulative = lastCum[val] || 0;
      }
    }
    if (key === 'wbs_id' && !(next[i].description || '').trim()) {
      const wbs = (meta?.wbs_activities || []).find(w => w.id === val);
      if (wbs) next[i].description = `${wbs.code || ''} · ${wbs.name}`;
    }
    setRows(next);
  };

  const addRow = () => setRows([...rows, { description: '', wbs_id: '', boq_id: '', daily_qty: 0, yesterday_cumulative: 0, boq_qty: 0 }]);
  const removeRow = (i) => setRows(rows.filter((_, idx) => idx !== i));

  const save = async () => {
    const noDesc = rows.find(r => !(r.description || '').trim());
    if (noDesc) { toast.error('Each activity needs a description.'); return; }
    setSaving(true);
    try {
      const { data, offline } = await postSection(projectId, date, 'execution', {
        activities: rows.map(r => ({
          description: (r.description || '').trim(),
          wbs_id: r.wbs_id || null, boq_id: r.boq_id || null,
          daily_qty: parseFloat(r.daily_qty) || 0,
          yesterday_cumulative: parseFloat(r.yesterday_cumulative) || 0,
          boq_qty: parseFloat(r.boq_qty) || 0,
          notes: r.notes,
        })),
      });
      onSaved(data);
      toast.success(offline ? `${deptLabel} section saved offline — will sync when back online` : `${deptLabel} section saved`);
    } catch (e) {
      const errs = e?.response?.data?.detail?.errors;
      toast.error(errs ? errs.join('; ') : 'Save failed');
    } finally { setSaving(false); }
  };

  return (
    <div className="bg-white border border-slate-200 rounded-sm" data-testid="dpr-section-execution">
      <div className="px-4 py-2.5 border-b border-slate-100 flex items-center justify-between">
        <div className="font-display font-bold text-sm uppercase tracking-wider text-slate-700">{deptLabel} · Activities</div>
        {readOnly
          ? <span className="text-[10px] font-display font-bold uppercase tracking-widest text-slate-400 border border-slate-200 rounded-sm px-2 py-0.5">Read only</span>
          : <button onClick={addRow} className="text-xs uppercase tracking-wider text-[#3b4872] font-semibold" data-testid="dpr-exec-add-row">+ Add line</button>}
      </div>
      <div className="overflow-x-auto">
        <table className="w-full dense-table text-xs">
          <thead>
            <tr>
              <th>Activity (what was done) *</th>
              <th>WBS Activity</th><th>BOQ Item</th>
              <th className="text-right">Yest. Cum.</th>
              <th className="text-right">Today Qty</th>
              <th className="text-right">BOQ Qty</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => {
              const unlinked = !(r.wbs_id && r.boq_id);
              return (
                <tr key={i}>
                  <td>
                    <div className="flex items-center gap-1">
                      {unlinked && <AlertTriangle size={13} className="text-amber-500 shrink-0" title="Not linked to WBS/BOQ" data-testid={`dpr-exec-unlinked-${i}`} />}
                      <input value={r.description || ''} onChange={(e) => updateRow(i, 'description', e.target.value)} placeholder="e.g. RCC column casting, grid B3–B6" className="border border-slate-200 rounded-sm text-xs px-2 py-1 bg-white w-full min-w-[180px]" data-testid={`dpr-exec-desc-${i}`} />
                    </div>
                  </td>
                  <td>
                    <select value={r.wbs_id || ''} onChange={(e) => updateRow(i, 'wbs_id', e.target.value)} data-testid={`dpr-exec-wbs-${i}`} className="border border-slate-200 rounded-sm text-xs px-2 py-1 bg-white w-full">
                      <option value="">— not linked —</option>
                      {(meta?.wbs_activities || []).map(w => <option key={w.id} value={w.id}>{w.code || ''} · {w.name}</option>)}
                    </select>
                  </td>
                  <td>
                    <select value={r.boq_id || ''} onChange={(e) => updateRow(i, 'boq_id', e.target.value)} data-testid={`dpr-exec-boq-${i}`} className="border border-slate-200 rounded-sm text-xs px-2 py-1 bg-white w-full">
                      <option value="">— not linked —</option>
                      {(meta?.boq_items || []).map(b => <option key={b.id} value={b.id}>{b.item_code || ''} · {b.description}</option>)}
                    </select>
                  </td>
                  <td className="text-right tabular-nums">{r.yesterday_cumulative || 0}</td>
                  <td className="text-right">
                    <input type="number" step="any" value={r.daily_qty} onChange={(e) => updateRow(i, 'daily_qty', e.target.value)} className="border border-slate-200 rounded-sm text-xs px-2 py-1 bg-white w-24 text-right tabular-nums" data-testid={`dpr-exec-qty-${i}`} />
                  </td>
                  <td className="text-right tabular-nums">{r.boq_qty || 0}</td>
                  <td><button onClick={() => removeRow(i)} className="text-xs text-rose-600" data-testid={`dpr-exec-remove-${i}`}>×</button></td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <div className="px-4 py-2 text-[11px] text-amber-700 bg-amber-50/50 border-t border-slate-100">
        <AlertTriangle size={11} className="inline mr-1" /> Rows without a WBS/BOQ link won't roll up into quantity tracking — link them when you can.
      </div>
      {!readOnly && (
        <div className="px-4 py-3 border-t border-slate-100 flex justify-end">
          <button onClick={save} disabled={saving} className="text-sm font-display font-semibold uppercase tracking-wider px-4 py-2 rounded-sm bg-[#3b4872] text-white disabled:bg-slate-300" data-testid="dpr-exec-save">
            {saving ? 'Saving…' : 'Save section'} <ArrowRight className="inline ml-1" size={14} />
          </button>
        </div>
      )}
    </div>
  );
}
